import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, PropertyCard as ConstellationPropertyCard, Icon, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconFileOutline, IconMailOutline, IconSettingsOutline } from '@zillow/constellation-icons';

import { LoadingHeading, LoadingImage, LoadingParagraph } from '@zg-rentals/loading-components';

import { useScreenSize } from '../../hooks';
import { useGetListingDetails } from '../../hooks/queries/properties';
import numberUtils from '../../utils/numberUtils';
import propertyUtils from '../../utils/propertyUtils';

import { BEHAVIOR_OPTIONS } from '../../components/modals/AddPropertyModal/constants';

import { propertyPhotoStyles } from '../../../srcLegacy/shared/components/WizardModularListing/Review/styles';
import { useAuth } from '../../components/AuthProvider';
import AddPropertyModal from '../../components/modals/AddPropertyModal';

import { singleTextLineStyles } from '../../styles/text';

import { emitApplicationsClickEvent, EVENT_IDS } from '../../analytics/events/reviewPage';

const PropertyCard = ({ isSkeleton = false }: { isSkeleton?: boolean }) => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const [displayAddPropertyModal, setDisplayAddPropertyModal] = useState(false);

  const { listingAlias } = router.query as { listingAlias: string };

  const { data: listingDetails, isPending: isListingDetailsLoading } = useGetListingDetails(listingAlias, {
    enabled: !isSkeleton,
  });

  const isLoading = isSkeleton || isListingDetailsLoading;

  const {
    listingAlias: applicationsSummaryListingAlias = '',
    rentalApplicationData = {
      totalNumberOfActiveApplications: 0,
      totalNumberOfWithdrawnApplications: 0,
      totalNumberOfDeclinedApplications: 0,
    },
  } = listingDetails?.applicationsSummary || {};

  const { totalNumberOfActiveApplications, totalNumberOfWithdrawnApplications, totalNumberOfDeclinedApplications } =
    rentalApplicationData;

  const { formattedStreet, formattedCompleteAddress } = propertyUtils.formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
    unit: listingDetails?.unit,
  });

  const textButtonSize = isScreenMobile ? 'sm' : 'md';

  const totalNumberOfAllApplications =
    totalNumberOfDeclinedApplications + totalNumberOfWithdrawnApplications + totalNumberOfActiveApplications;

  const handleApplicationsClick = () => {
    if (listingDetails && user) {
      emitApplicationsClickEvent({
        currentProperty: listingDetails,
        user,
      });
    }
  };

  const renderAddressDetails = () => {
    const lowestPrice =
      listingDetails?.floorPlans && listingDetails?.floorPlans?.length > 0
        ? listingDetails.floorPlans.reduce<number | null>((min, item) => {
            const planPrice = item.lowPrice;
            if (planPrice && planPrice > 0) {
              return min === null || planPrice < min ? planPrice : min;
            }
            return min;
          }, null)
        : null;

    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Box>
          <LoadingHeading level={3} css={{ textWrap: 'balance' }} isLoading={isLoading} loadingWidth="25%">
            {listingDetails?.name || formattedStreet}
          </LoadingHeading>
          <LoadingParagraph css={{ ...singleTextLineStyles }} isLoading={isLoading} loadingWidth="33%">
            {formattedCompleteAddress}
          </LoadingParagraph>
          {lowestPrice && (
            <Paragraph textStyle="body-bold" css={{ marginTop: '300' }}>
              Rent starting at {numberUtils.asCurrency(lowestPrice)}
            </Paragraph>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      css={{
        display: 'flex',
        mdDown: { flexDirection: 'column', alignItems: 'start' },
        minHeight: { base: '74px', md: '156px' },
      }}
    >
      {!isScreenMobile && (
        <Box css={{ overflow: 'hidden', ...propertyPhotoStyles }}>
          <ConstellationPropertyCard.Photo
            src={listingDetails?.primaryPhoto?.url ?? undefined}
            alt="property"
            fallbackImg={
              isLoading ? (
                <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
              ) : (
                <ConstellationPropertyCard.PhotoFallback />
              )
            }
            css={{ width: '100%', height: '104px' }}
          />
        </Box>
      )}
      {renderAddressDetails()}

      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginLeft: { lg: '200' },
          marginTop: { mdDown: '200' },
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', lg: 'row' },
            gap: { lg: '400' },
            justifyContent: 'flex-end',
          }}
        >
          {user?.features?.canAccessUserManagementApplicationPermission && applicationsSummaryListingAlias !== '' && (
            <TextButton
              tone="neutral"
              icon={<Icon render={<IconFileOutline />} />}
              size={textButtonSize}
              onClick={handleApplicationsClick}
              data-eventid={EVENT_IDS.APPLICATIONS_CLICK}
              asChild={true}
            >
              <a href={`/rental-manager/applications/property/${listingAlias}`}>
                Applications ({totalNumberOfAllApplications})
              </a>
            </TextButton>
          )}

          {listingDetails?.lastActivatedMs && (
            <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
              <TextButton
                tone="neutral"
                icon={<Icon render={<IconMailOutline />} />}
                size={textButtonSize}
                asChild={true}
              >
                <a href={`/rental-manager/inbox/${listingAlias}?byProperty=true`}>Messages</a>
              </TextButton>
            </Box>
          )}
          {user?.isPostingPathAdmin && (
            <TextButton
              tone="neutral"
              icon={<Icon render={<IconSettingsOutline />} />}
              size={textButtonSize}
              onClick={() => setDisplayAddPropertyModal(true)}
            >
              Admin tools
            </TextButton>
          )}
        </Box>
      </Box>

      {displayAddPropertyModal && (
        <AddPropertyModal
          isOpen={true}
          onClose={() => setDisplayAddPropertyModal(false)}
          behavior={BEHAVIOR_OPTIONS.EDIT}
        />
      )}
    </Box>
  );
};

export default PropertyCard;
